import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Landmark, Plus, Loader2, Trash2, CheckCircle2, AlertTriangle, Clock, RefreshCw,
} from 'lucide-react';
import { accountingAPI, carrierAPI } from '@/lib/api';
import { toast } from 'sonner';

const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STATUS_META = {
  confirmed: { label: 'Confirmed', tone: 'bg-emerald-100 text-emerald-800 border-emerald-200', icon: CheckCircle2 },
  variance:  { label: 'Variance — blocked', tone: 'bg-red-100 text-red-800 border-red-200', icon: AlertTriangle },
  pending:   { label: 'Pending', tone: 'bg-slate-100 text-slate-700 border-slate-200', icon: Clock },
};

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.pending;
  const Icon = m.icon;
  return (
    <Badge className={`${m.tone} border font-normal gap-1`} data-testid={`deposit-status-${status}`}>
      <Icon size={12} /> {m.label}
    </Badge>
  );
}

export default function BankDepositsPage() {
  const [carriers, setCarriers] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try { const { data } = await carrierAPI.list(); setCarriers(data || []); } catch { /* noop */ }
    })();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await accountingAPI.listBankDeposits();
      setDeposits(data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load bank deposits');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    try {
      await accountingAPI.deleteBankDeposit(id);
      toast.success('Bank deposit removed — linked imports released');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-5" data-testid="bank-deposits-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-heading font-semibold flex items-center gap-2">
            <Landmark size={22} className="text-sky-600" /> Bank Deposits
          </h1>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            Match one bank deposit to the statements it paid. When a single carrier payment
            groups several statements, link them here — the expected amounts are summed and
            compared to what actually landed in the bank. A variance <b>blocks paying</b> every
            statement in the group until it's resolved.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} data-testid="refresh-btn"><RefreshCw size={14} className="mr-1" /> Refresh</Button>
          <Button onClick={() => setDialogOpen(true)} data-testid="new-deposit-btn"><Plus size={14} className="mr-1" /> New Grouped Deposit</Button>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">{loading ? 'Loading…' : `${deposits.length} bank deposit(s)`}</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-10 text-center text-slate-400"><Loader2 className="mx-auto animate-spin" /></div>
          ) : deposits.length === 0 ? (
            <div className="py-10 text-center text-slate-400" data-testid="deposits-empty">
              No grouped bank deposits yet. Click <b>New Grouped Deposit</b> to match a bank payment to its statements.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-slate-500 border-b">
                    <th className="py-2 pr-3">Carrier</th>
                    <th className="py-2 pr-3">Bank date</th>
                    <th className="py-2 pr-3 text-right">Bank amount</th>
                    <th className="py-2 pr-3 text-right">Expected</th>
                    <th className="py-2 pr-3 text-right">Variance</th>
                    <th className="py-2 pr-3 text-center">Statements</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2 pr-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {deposits.map((d) => (
                    <tr key={d.deposit_id} className="border-b border-slate-100" data-testid={`deposit-row-${d.deposit_id}`}>
                      <td className="py-2 pr-3 font-medium">{d.carrier_name || d.carrier_id}</td>
                      <td className="py-2 pr-3">{(d.bank_date || '').slice(0, 10) || '—'}</td>
                      <td className="py-2 pr-3 text-right">{money(d.bank_amount)}</td>
                      <td className="py-2 pr-3 text-right text-slate-600">{money(d.expected_total)}</td>
                      <td className={`py-2 pr-3 text-right font-medium ${Math.abs(d.variance || 0) >= 0.01 ? 'text-red-600' : 'text-emerald-600'}`}>
                        {d.variance === null || d.variance === undefined ? '—' : `${d.variance > 0 ? '+' : ''}${money(d.variance)}`}
                      </td>
                      <td className="py-2 pr-3 text-center">{d.import_count}</td>
                      <td className="py-2 pr-3"><StatusBadge status={d.status} /></td>
                      <td className="py-2 pr-3 text-right">
                        <Button variant="ghost" size="sm" onClick={() => remove(d.deposit_id)} data-testid={`delete-deposit-${d.deposit_id}`}>
                          <Trash2 size={14} className="text-slate-400 hover:text-red-500" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <NewDepositDialog
        open={dialogOpen}
        carriers={carriers}
        onClose={() => setDialogOpen(false)}
        onCreated={() => { setDialogOpen(false); load(); }}
      />
    </div>
  );
}

function NewDepositDialog({ open, carriers, onClose, onCreated }) {
  const [carrierId, setCarrierId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [selected, setSelected] = useState({});
  const [bankAmount, setBankAmount] = useState('');
  const [bankDate, setBankDate] = useState('');
  const [note, setNote] = useState('');
  const [loadingSug, setLoadingSug] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setCarrierId(''); setDateFrom(''); setDateTo(''); setSuggestions([]);
      setSelected({}); setBankAmount(''); setBankDate(''); setNote('');
    }
  }, [open]);

  const fetchSuggestions = useCallback(async () => {
    if (!carrierId) { toast.error('Pick a carrier first'); return; }
    setLoadingSug(true);
    try {
      const params = { carrier_id: carrierId };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data } = await accountingAPI.suggestBankDepositImports(params);
      setSuggestions(data.items || []);
      // pre-select all suggested imports
      const pre = {};
      (data.items || []).forEach((i) => { pre[i.import_id] = true; });
      setSelected(pre);
      if ((data.items || []).length === 0) toast.info('No unreconciled imports found for that carrier / range');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load suggestions');
    } finally { setLoadingSug(false); }
  }, [carrierId, dateFrom, dateTo]);

  const selectedIds = useMemo(() => suggestions.filter((s) => selected[s.import_id]).map((s) => s.import_id), [suggestions, selected]);
  const expectedTotal = useMemo(
    () => suggestions.filter((s) => selected[s.import_id]).reduce((acc, s) => acc + Number(s.expected || 0), 0),
    [suggestions, selected],
  );
  const variance = bankAmount === '' ? null : Number(bankAmount) - expectedTotal;

  const create = async () => {
    if (!selectedIds.length) { toast.error('Select at least one statement/import'); return; }
    if (bankAmount === '' || isNaN(Number(bankAmount))) { toast.error('Enter the bank deposit amount'); return; }
    setSaving(true);
    try {
      await accountingAPI.createBankDeposit({
        carrier_id: carrierId,
        bank_amount: Number(bankAmount),
        bank_date: bankDate || null,
        note: note || null,
        import_ids: selectedIds,
      });
      const matches = Math.abs(Number(bankAmount) - expectedTotal) < 0.01;
      toast.success(matches ? 'Deposit confirmed — statements unblocked' : 'Deposit saved with a variance — statements stay blocked');
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create bank deposit');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-hidden flex flex-col" data-testid="new-deposit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Landmark size={18} /> New Grouped Bank Deposit</DialogTitle>
          <DialogDescription>
            Pick the carrier and the date range of the deposit, select the statements it paid,
            then enter what actually landed in the bank.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-2 space-y-4">
          {/* Carrier + date range */}
          <div className="grid gap-3 md:grid-cols-4 items-end">
            <div className="md:col-span-2">
              <Label className="text-xs">Carrier</Label>
              <Select value={carrierId} onValueChange={setCarrierId}>
                <SelectTrigger data-testid="deposit-carrier"><SelectValue placeholder="Select carrier" /></SelectTrigger>
                <SelectContent>
                  {carriers.map((c) => <SelectItem key={c.carrier_id} value={c.carrier_id}>{c.carrier_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">From (uploaded)</Label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="deposit-date-from" />
            </div>
            <div>
              <Label className="text-xs">To (uploaded)</Label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="deposit-date-to" />
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={fetchSuggestions} disabled={loadingSug || !carrierId} data-testid="suggest-btn">
            {loadingSug ? <Loader2 size={14} className="mr-1 animate-spin" /> : <RefreshCw size={14} className="mr-1" />} Suggest statements
          </Button>

          {/* Suggestions */}
          {suggestions.length > 0 && (
            <div className="border rounded-md overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b">
                  <tr className="text-slate-600 text-left">
                    <th className="px-3 py-2 w-8"></th>
                    <th className="px-3 py-2 font-medium">Period</th>
                    <th className="px-3 py-2 font-medium">Status</th>
                    <th className="px-3 py-2 font-medium text-right">Expected</th>
                  </tr>
                </thead>
                <tbody>
                  {suggestions.map((s) => (
                    <tr key={s.import_id} className="border-b border-slate-100 last:border-0" data-testid={`suggest-row-${s.import_id}`}>
                      <td className="px-3 py-2">
                        <Checkbox
                          checked={!!selected[s.import_id]}
                          onCheckedChange={(v) => setSelected((prev) => ({ ...prev, [s.import_id]: !!v }))}
                          data-testid={`suggest-check-${s.import_id}`}
                        />
                      </td>
                      <td className="px-3 py-2">{s.period_label || s.import_id}</td>
                      <td className="px-3 py-2"><Badge variant="outline" className="capitalize">{s.status}</Badge></td>
                      <td className="px-3 py-2 text-right">{money(s.expected)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Bank amount + live reconciliation */}
          <div className="grid gap-3 md:grid-cols-3">
            <div>
              <Label className="text-xs">Bank deposit amount</Label>
              <Input type="number" step="0.01" value={bankAmount} onChange={(e) => setBankAmount(e.target.value)}
                placeholder="0.00" data-testid="deposit-amount" />
            </div>
            <div>
              <Label className="text-xs">Bank date</Label>
              <Input type="date" value={bankDate} onChange={(e) => setBankDate(e.target.value)} data-testid="deposit-bank-date" />
            </div>
            <div>
              <Label className="text-xs">Note (optional)</Label>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={1} data-testid="deposit-note" />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3 text-sm">
            <div className="rounded-md border bg-slate-50 p-2.5">
              <div className="text-[10px] uppercase tracking-wide text-slate-500">Selected</div>
              <div className="font-medium">{selectedIds.length} statement(s)</div>
            </div>
            <div className="rounded-md border bg-slate-50 p-2.5">
              <div className="text-[10px] uppercase tracking-wide text-slate-500">Expected total</div>
              <div className="font-medium" data-testid="expected-total">{money(expectedTotal)}</div>
            </div>
            <div className={`rounded-md border p-2.5 ${variance === null ? 'bg-slate-50' : Math.abs(variance) < 0.01 ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
              <div className="text-[10px] uppercase tracking-wide text-slate-500">Variance</div>
              <div className={`font-medium ${variance === null ? '' : Math.abs(variance) < 0.01 ? 'text-emerald-700' : 'text-red-700'}`} data-testid="live-variance">
                {variance === null ? '—' : `${variance > 0 ? '+' : ''}${money(variance)}`}
              </div>
            </div>
          </div>
          {variance !== null && Math.abs(variance) >= 0.01 && (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-2.5 text-xs text-amber-900 flex gap-2">
              <AlertTriangle size={14} className="shrink-0 mt-0.5" />
              The bank amount doesn't match the expected total. You can still save it, but every statement
              in this group stays blocked for payment until the variance is resolved.
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={create} disabled={saving || !selectedIds.length || bankAmount === ''} data-testid="create-deposit-btn">
            {saving ? <Loader2 size={14} className="mr-1 animate-spin" /> : <CheckCircle2 size={14} className="mr-1" />} Save deposit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
